/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import { type IEvent } from '../../reducers/eventsSlice';
import { classNames } from '../../utilities/styleUtils';

interface EventProgressStageProps {
  event: IEvent;
}

enum StageType {
  BUILD = 'build',
  START = 'start',
  END = 'end',
  DECOMMISSION = 'decommission',
}

interface Stage {
  type: StageType;
  date: Date;
  label: string;
}

const EventTimelineProgress: React.FC<EventProgressStageProps> = ({ event }) => {
  const [hoveredStage, setHoveredStage] = useState<StageType | null>(null);

  const stages: Stage[] = [
    {
      type: StageType.BUILD,
      date: new Date(event.build_datetime),
      label: 'Build',
    },
    {
      type: StageType.START,
      date: new Date(event.start_datetime),
      label: 'Start',
    },
    {
      type: StageType.END,
      date: new Date(event.end_datetime),
      label: 'End',
    },
    {
      type: StageType.DECOMMISSION,
      date: new Date(event.decommission_datetime),
      label: 'Decommission',
    },
  ];

  const now = new Date();

  const getCurrentStage = (): { currentStage: StageType; progress: number } => {
    const buildDate = stages[0].date;
    const startDate = stages[1].date;
    const endDate = stages[2].date;
    const decommissionDate = stages[3].date;

    if (now < buildDate) {
      // Before build stage
      return { currentStage: StageType.BUILD, progress: 0 };
    } else if (now >= buildDate && now < startDate) {
      // Build stage
      return { currentStage: StageType.BUILD, progress: 1 };
    } else if (now >= startDate && now < endDate) {
      // Event in progress
      return { currentStage: StageType.START, progress: 2 };
    } else if (now >= endDate && now < decommissionDate) {
      // Event ended, awaiting decommission
      return { currentStage: StageType.END, progress: 3 };
    } else {
      // Decommissioned
      return { currentStage: StageType.DECOMMISSION, progress: 4 };
    }
  };

  const { currentStage, progress } = getCurrentStage();

  const getStageStyle = (index: number): string => {
    const baseClasses = 'w-3 h-3 rounded-full border-2 transition-all duration-200';

    if (index < progress) {
      // Completed stages
      return `${baseClasses} bg-dataops-yale-blue border-dataops-yale-blue`;
    } else if (index === progress) {
      // Current stage
      return `${baseClasses} bg-white border-dataops-yale-blue relative`;
    } else {
      // Future stages
      return `${baseClasses} bg-white border-gray-300`;
    }
  };

  const getConnectorStyle = (index: number): string => {
    const baseClasses = 'flex-1 h-0.5 mx-1';

    if (index < progress) {
      return `${baseClasses} bg-dataops-yale-blue`;
    } else {
      return `${baseClasses} bg-dataops-taupe-gray`;
    }
  };

  const getDescriptionStyle = (): string => {
    switch (currentStage) {
      case StageType.BUILD:
        return '';
      case StageType.START:
        return 'animated-stripes';
      case StageType.END:
        return '';
      case StageType.DECOMMISSION:
        return '';
      default:
        return '';
    }
  };

  const getRelativeTimeText = (): string => {
    const currentStageData = stages.find((stage) => stage.type === currentStage);
    if (currentStageData === null || currentStageData === undefined) return '';

    const timeDiff = currentStageData.date.getTime() - now.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    switch (currentStage) {
      case StageType.BUILD:
        if (progress === 0) {
          return daysDiff > 0 ? `Builds in ${daysDiff} day${daysDiff !== 1 ? 's' : ''}` : 'Building now';
        } else {
          const startDiff = Math.ceil((stages[1].date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          return startDiff > 0 ? `Starts in ${startDiff} day${startDiff !== 1 ? 's' : ''}` : 'Starting now';
        }
      case StageType.START:
        return 'Event in progress';
      case StageType.END: {
        const decommissionDiff = Math.ceil((stages[3].date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        return decommissionDiff > 0
          ? `Decommissions in ${decommissionDiff} day${decommissionDiff !== 1 ? 's' : ''}`
          : 'Decommissioning now';
      }
      case StageType.DECOMMISSION:
        return 'Event decommissioned';
      default:
        return '';
    }
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="relative min-w-44 py-0.5">
      {/* Progress indicator */}
      <div className="flex items-center mb-2">
        {stages.map((stage, index) => (
          <React.Fragment key={stage.type}>
            <div
              className="relative"
              onMouseEnter={() => {
                setHoveredStage(stage.type);
              }}
              onMouseLeave={() => {
                setHoveredStage(null);
              }}
            >
              <div className={getStageStyle(index)}>
                {/* Current stage indicator */}
                {index === progress && <div className="absolute inset-1 bg-dataops-yale-blue rounded-full"></div>}
                {/* Partial progress indicator for current stage */}
                {index === progress && progress > 0 && (
                  <div className="absolute inset-0 rounded-full border-2 border-dataops-yale-blue">
                    <div className="w-full h-full bg-dataops-yale-blue rounded-full opacity-20"></div>
                  </div>
                )}
              </div>

              {/* Tooltip */}
              {hoveredStage === stage.type && (
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-dataops-rich-black text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
                  <div className="font-medium">{stage.label}</div>
                  <div>{formatDate(stage.date)}</div>
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-2 border-r-2 border-t-2 border-transparent border-t-dataops-rich-black"></div>
                </div>
              )}
            </div>

            {/* Connector line */}
            {index < stages.length - 1 && <div className={getConnectorStyle(index)}></div>}
          </React.Fragment>
        ))}
      </div>

      {/* Description text */}
      <div
        className={classNames('text-xs text-dataops-yale-blue px-2 rounded-full text-center', getDescriptionStyle())}
      >
        <span className="">{getRelativeTimeText()}</span>
      </div>
    </div>
  );
};

export default EventTimelineProgress;
